// src/components/ui/FileUpload.tsx
import React, { useState, useRef, useCallback } from 'react';
import {
  Upload,
  X,
  FileText,
  Image,
  FileSpreadsheet,
  File,
  AlertCircle,
  CheckCircle,
  Loader2,
} from 'lucide-react';

// ==================== Types ====================
interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  preview?: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

interface FileUploadProps {
  label?: string;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number; // in bytes
  files: UploadedFile[];
  onChange: (files: UploadedFile[] | ((prev: UploadedFile[]) => UploadedFile[])) => void;
  onUpload?: (file: UploadedFile) => Promise<void>;
  disabled?: boolean;
  helpText?: string;
  error?: string;
  compact?: boolean;
  className?: string;
}

// ==================== Helper Functions ====================
const generateId = () => Math.random().toString(36).substr(2, 9);

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const getFileIcon = (mimeType: string): React.ReactNode => {
  if (mimeType.startsWith('image/')) {
    return <Image className="w-5 h-5 text-purple-500" />;
  }
  if (mimeType.includes('pdf')) {
    return <FileText className="w-5 h-5 text-danger" />;
  }
  if (mimeType.includes('sheet') || mimeType.includes('excel') || mimeType.includes('csv')) {
    return <FileSpreadsheet className="w-5 h-5 text-success" />;
  }
  if (mimeType.includes('word') || mimeType.includes('document')) {
    return <FileText className="w-5 h-5 text-info" />;
  }
  return <File className="w-5 h-5 text-neutral-text-secondary" />;
};

const getAcceptLabel = (accept?: string): string => {
  if (!accept) return 'Semua file';
  const types = accept.split(',').map((t) => t.trim());
  const labels: string[] = [];

  types.forEach((type) => {
    if (type === '.pdf') labels.push('PDF');
    else if (type === '.doc' || type === '.docx') labels.push('DOC');
    else if (type === '.xls' || type === '.xlsx') labels.push('XLS');
    else if (type === '.jpg' || type === '.jpeg') labels.push('JPG');
    else if (type === '.png') labels.push('PNG');
    else if (type === '.csv') labels.push('CSV');
    else labels.push(type.replace('.', '').toUpperCase());
  });

  return labels.join(', ');
};

// ==================== Component ====================
const FileUpload: React.FC<FileUploadProps> = ({
  label,
  accept = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png',
  multiple = true,
  maxFiles = 10,
  maxSize = 10 * 1024 * 1024, // 10MB default
  files,
  onChange,
  onUpload,
  disabled = false,
  helpText,
  error,
  compact = false,
  className = '',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // ==================== Validation ====================
  const validateFile = (file: File): string | null => {
    // Check size
    if (file.size > maxSize) {
      return `File melebihi batas maksimal ${formatFileSize(maxSize)}`;
    }

    // Check accept types
    if (accept) {
      const acceptTypes = accept.split(',').map((t) => t.trim().toLowerCase());
      const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
      const fileMime = file.type.toLowerCase();

      const isValid = acceptTypes.some((type) => {
        if (type.startsWith('.')) return fileExt === type;
        if (type.includes('*')) return fileMime.startsWith(type.replace('*', ''));
        return fileMime === type;
      });

      if (!isValid) {
        return `Tipe file tidak diizinkan. Format: ${getAcceptLabel(accept)}`;
      }
    }

    return null;
  };

  // ==================== Add Files ====================
  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const fileArray = Array.from(newFiles);

      // Check max files
      if (files.length + fileArray.length > maxFiles) {
        alert(`Maksimal ${maxFiles} file per upload`);
        return;
      }

      const processedFiles: UploadedFile[] = [];

      fileArray.forEach((file) => {
        const error = validateFile(file);

        const uploadedFile: UploadedFile = {
          id: generateId(),
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          preview: file.type.startsWith('image/')
            ? URL.createObjectURL(file)
            : undefined,
          status: error ? 'error' : 'pending',
          progress: 0,
          error: error || undefined,
        };

        processedFiles.push(uploadedFile);
      });

      const updatedFiles = [...files, ...processedFiles];
      onChange(updatedFiles);

      // Auto upload if handler provided
      if (onUpload) {
        processedFiles
          .filter((f) => f.status === 'pending')
          .forEach(async (f) => {
            try {
              // Update status to uploading
              const uploading = updatedFiles.map((uf) =>
                uf.id === f.id ? { ...uf, status: 'uploading' as const, progress: 50 } : uf
              );
              onChange(uploading);

              await onUpload(f);

              // Update status to success
              onChange((prev: UploadedFile[]) =>
                prev.map((uf) =>
                  uf.id === f.id ? { ...uf, status: 'success' as const, progress: 100 } : uf
                )
              );
            } catch (err) {
              onChange((prev: UploadedFile[]) =>
                prev.map((uf) =>
                  uf.id === f.id
                    ? { ...uf, status: 'error' as const, error: 'Upload gagal' }
                    : uf
                )
              );
            }
          });
      }
    },
    [files, maxFiles, maxSize, accept, onChange, onUpload]
  );

  // ==================== Remove File ====================
  const removeFile = (id: string) => {
    const file = files.find((f) => f.id === id);
    if (file?.preview) {
      URL.revokeObjectURL(file.preview);
    }
    onChange(files.filter((f) => f.id !== id));
  };

  // ==================== Drag & Drop ====================
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!disabled && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  // ==================== Input Change ====================
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = ''; // Reset input
    }
  };

  // ==================== Status Icon ====================
  const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
      case 'uploading':
        return <Loader2 className="w-4 h-4 text-teal-main animate-spin" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-success" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-danger" />;
      default:
        return null;
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
          {label}
        </label>
      )}

      {/* Drop Zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`
          relative flex flex-col items-center justify-center
          border-2 border-dashed rounded-xl transition-all cursor-pointer
          ${compact ? 'h-24 px-4' : 'h-40 px-6'}
          ${isDragging
            ? 'border-teal-main bg-teal-main/5 scale-[1.01]'
            : error
              ? 'border-danger/50 bg-danger/5 hover:border-danger'
              : 'border-neutral-border bg-white hover:border-teal-light hover:bg-teal-main/5'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleInputChange}
          disabled={disabled}
          className="hidden"
        />

        <Upload
          className={`
            ${compact ? 'w-6 h-6 mb-1' : 'w-8 h-8 mb-2'}
            ${isDragging ? 'text-teal-main' : 'text-neutral-text-secondary'}
          `}
        />

        {!compact && (
          <>
            <p className="text-sm font-medium text-neutral-text-primary">
              {isDragging ? 'Lepaskan file di sini' : 'Klik untuk upload atau drag & drop'}
            </p>
            <p className="text-xs text-neutral-text-secondary mt-1">
              {getAcceptLabel(accept)} (Maks {formatFileSize(maxSize)})
            </p>
          </>
        )}

        {compact && (
          <p className="text-xs text-neutral-text-secondary">
            {isDragging ? 'Lepaskan file' : `Drop file atau klik (${getAcceptLabel(accept)})`}
          </p>
        )}

        {/* Files count */}
        {files.length > 0 && (
          <p className="text-[10px] text-neutral-text-secondary mt-1">
            {files.length}/{maxFiles} file
          </p>
        )}
      </div>

      {/* Error message */}
      {error && <p className="text-xs text-danger">{error}</p>}

      {/* Help text */}
      {helpText && !error && (
        <p className="text-xs text-neutral-text-secondary">{helpText}</p>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2 mt-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
            File Terupload ({files.length}/{maxFiles})
          </p>

          {files.map((file) => (
            <div
              key={file.id}
              className={`
                flex items-center gap-3 p-3 rounded-lg border transition-colors
                ${file.status === 'error'
                  ? 'bg-danger/5 border-danger/20'
                  : file.status === 'success'
                    ? 'bg-success/5 border-success/20'
                    : file.status === 'uploading'
                      ? 'bg-teal-main/5 border-teal-main/20'
                      : 'bg-neutral-bg border-neutral-border'
                }
              `}
            >
              {/* Preview / Icon */}
              {file.preview ? (
                <img
                  src={file.preview}
                  alt={file.name}
                  className="w-10 h-10 rounded-lg object-cover shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-white border border-neutral-border flex items-center justify-center shrink-0">
                  {getFileIcon(file.type)}
                </div>
              )}

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-neutral-text-primary truncate">
                    {file.name}
                  </p>
                  {getStatusIcon(file.status)}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="text-xs text-neutral-text-secondary">
                    {formatFileSize(file.size)}
                  </p>
                  {file.error && (
                    <p className="text-xs text-danger">{file.error}</p>
                  )}
                </div>

                {/* Upload Progress */}
                {file.status === 'uploading' && (
                  <div className="w-full bg-gray-200 rounded-full h-1 mt-1.5 overflow-hidden">
                    <div
                      className="bg-teal-light h-1 rounded-full transition-all duration-500"
                      style={{ width: `${file.progress}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Remove Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(file.id);
                }}
                disabled={file.status === 'uploading'}
                className={`
                  p-1.5 rounded-lg shrink-0 transition-colors
                  ${file.status === 'uploading'
                    ? 'opacity-30 cursor-not-allowed'
                    : 'hover:bg-danger/10 text-neutral-text-secondary hover:text-danger'
                  }
                `}
                title="Hapus file"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;